<template>
    <span class="kpis">
        <div class="kpis-carousel">
            <button class="kpis-carousel-prev-next-arrow left"
                    v-if="showArrows"
                    :disabled="reachedMaxLeft"
                    @click="moveLeft">
                <IconBase width="8"
                          height="14"
                          iconName=arrowRightIcon
                          :iconColor="prevIconColor">
                </IconBase>
            </button>
            <transition-group tag="div" :name="transitionName" class="list-transition-container">
                <div :key="animationId" v-for="animationId in animationArray"
                     class="list-transition-item"
                     :class="{'show-arrows': showArrows}">
                    <div class="kpis-kpis-container" :style="kpisContainerStyle">
                        <KPI :value="kpi.value"
                             @click="changeSelected(index)"
                             class="kpi"
                             v-for="(kpi, index) in visibleKPIs"
                             :key="index"
                             :previousValue="kpi.previousValue"
                             :isToday="isToday"
                             :id="kpi.id"
                             :selected="index === selectedKPIIndex"
                             :disabled="kpi.disabled"
                             :currency="currency"
                             :previousTimeFrame="previousTimeFrame"
                             :isFirst="index === firstShownKpi">
                        </KPI>
                    </div>
                </div>
            </transition-group>
            <button class="kpis-carousel-prev-next-arrow right"
                    v-if="showArrows"
                    :disabled="reachedMaxRight"
                    @click="moveRight">
                <IconBase
                    width="8"
                    height="14"
                    iconName=arrowRightIcon
                    :iconColor="nextIconColor">
                </IconBase>
            </button>
        </div>
    </span>
</template>

<script>

import consts from 'services/metaData/consts';
import IconBase from 'components/IconBase.vue';
import KPI from 'monetize/reports/components/kpis/KPI.vue';

export default {
    name: 'KPIs',
    components: { KPI, IconBase },
    props: {
        numOfShownKpis: {
            type: Number,
            default: 5
        },
        isToday: Boolean,
        kpisArr: {
            type: Array,
            required: true
        },
        selectedKPI: {
            type: String
        },
        page: {
            type: Number,
            required: true
        },
        previousTimeFrame: String,
        currency: String,
        showArrows: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            firstShownKpi: 0,
            transitionName: undefined,
            animationArray: [0]
        };
    },
    watch: {
        selectedKPI() {
            if (!this.visibleSelectedKPI) {
                this.firstShownKpi = this.getFirstShownKpi(this.page);
            }
        },
        kpisArr() {
            if (!this.visibleSelectedKPI) {
                this.firstShownKpi = this.getFirstShownKpi(this.page);
            }
            this.animationArray = [this.firstShownKpi];
        },
        page: {
            handler(newPage) {
                if (newPage !== this.currentPage) {
                    const nextFirstShownKpi = this.getFirstShownKpi(newPage);
                    this.transitionName = nextFirstShownKpi > this.firstShownKpi ? 'slide-next' : 'slide-prev';
                    this.firstShownKpi = nextFirstShownKpi;
                    this.animateContainer();
                }
            },
            immediate: true
        }
    },
    computed: {
        visibleKPIs() {
            return this.kpisArr.slice(this.firstShownKpi, this.firstShownKpi + this.numOfShownKpis).filter(Boolean);
        },
        visibleSelectedKPI() {
            return this.visibleKPIs.find((kpi) => kpi.id === this.selectedKPI);
        },
        selectedKPIIndex() {
            return this.visibleSelectedKPI ? this.visibleKPIs.indexOf(this.visibleSelectedKPI) : 0;
        },
        reachedMaxRight() {
            return this.kpisArr.length <= (this.firstShownKpi + this.numOfShownKpis);
        },
        reachedMaxLeft() {
            return this.firstShownKpi === 0;
        },
        kpisContainerStyle() {
            return `grid-template-columns: repeat(${this.numOfShownKpis}, 1fr)`;
        },
        nextIconColor() {
            if (this.reachedMaxRight) {
                return consts.COLORS.LIGHT_700;
            }
            return consts.COLORS.PRIMARY_500;
        },
        prevIconColor() {
            if (this.reachedMaxLeft) {
                return consts.COLORS.LIGHT_700;
            }
            return consts.COLORS.PRIMARY_500;
        },
        currentPage() {
            return Math.floor(this.firstShownKpi / this.numOfShownKpis);
        }
    },
    methods: {
        getFirstShownKpi(page) {
            return page * this.numOfShownKpis;
        },
        animateContainer() {
            this.animationArray.pop();
            this.animationArray.push(this.firstShownKpi);
        },
        changeSelected(visibleIndex) {
            const newIndex = this.firstShownKpi + visibleIndex;
            this.kpisArr[this.selectedKPIIndex].selected = false;
            this.kpisArr[newIndex].selected = true;
            this.$emit('update:selectedKPI', this.kpisArr[newIndex].id);
        },
        notifySelectedKPIChanged() {
            this.$emit('update:selectedKPI', this.kpisArr[this.firstShownKpi].id);
            this.$emit('update:page', this.currentPage);
        },
        moveLeft() {
            this.firstShownKpi -= this.numOfShownKpis;
            this.notifySelectedKPIChanged();
            this.transitionName = 'slide-prev';
            this.animateContainer();
        },
        moveRight() {
            this.firstShownKpi += this.numOfShownKpis;
            this.notifySelectedKPIChanged();
            this.transitionName = 'slide-next';
            this.animateContainer();
        }
    }
};
</script>

<style scoped lang="scss">
    @import "~styles/styleGuide";
    @import "~styles/input";
    @import "~styles/screens";

    .kpis-carousel{
      height: 66px;
      position: relative;
      display: flex;
      border-radius: $borderRadius;
      overflow: hidden;
    }

    .kpi{
      width: 100%;
    }

    .kpis-kpis-container{
      overflow: hidden;
      position: relative;
      opacity:1;
      display: inline-grid;
      width: 100%;
      height: calc(100% - 2px);
      border-top: $border;
      background-color: $light-100;
      min-height: 70px;
    }

    .kpis-carousel-prev-next-arrow {
      cursor: pointer;
      padding: 24px 10px;
      border: $border;
      width: 30px;
      height: 100%;
      background-color: $white;
      display: inline-grid;
      position: absolute;
      outline: none;
      min-width: unset;
      background-image: none;
      z-index: getZIndexLayerOffset(normal, 1);

      &.right{
        border-top-right-radius: $borderRadius;
        right: 0;
      }

      &.left{
        border-bottom-right-radius: $borderRadius;
        left: 0;
        transform: rotate(180deg);
      }

      .disabled {
        cursor: default;
      }

      @media #{$mobile}{
        display: none;
      }
    }

    .list-transition-container {
      overflow: hidden;
      width: 100%;
      height: calc(100% - 2px);
    }

    .list-transition-item {
      position: absolute;
      width: 100%;
      border-left: $border;
      border-right: $border;
    }

    .show-arrows {
      width: calc(100% - 60px);
      left: 30px;
      border: none;
    }

    .slide-next-enter-active,
    .slide-next-leave-active {
      transition: transform 0.5s ease-in-out;
    }
    .slide-next-enter {
      transform: translate(100%);
    }
    .slide-next-leave-to {
      transform: translate(-100%);
    }

    .slide-prev-enter-active,
    .slide-prev-leave-active {
      transition: transform 0.5s ease-in-out;
    }
    .slide-prev-enter {
      transform: translate(-100%);
    }
    .slide-prev-leave-to {
      transform: translate(100%);
    }
</style>
